/** @jsxImportSource @emotion/react */
import React from "react";
import { Button, Form, Space, notification } from "antd";
import { TableRowSelection } from "antd/lib/table/interface";
import { ColumnConfig, EditableTableMode } from "./types";
import * as Styles from "./styles";

const openNotification = () => {
  const key = `open${Date.now()}`;
  const btn = (
    <Space>
      <Button
        type="primary"
        size="small"
        css={Styles.notificationButonStyle}
        onClick={() => notification.close(key)}
        icon={<div className="ico-check-white" style={{ marginRight: 10 }} />}
      >
        Confirm
      </Button>
    </Space>
  );
  notification.success({
    message: "Saved successfully",
    description: "Changes have been saved",
    btn,
    key,
    onClose: close,
    icon: <div className="ico-check-blue" css={Styles.iconSuccessStyle} />,
  });
};

/**
 * Inline (in-cell) row editing: edit mode toggle, form population, multi-row
 * propagation, and save. Mirrors the original page edit logic generically.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const useInlineEdit = <T extends object>(
  configs: ColumnConfig[],
  dataSource: T[],
  rowKey: string,
  onSave: (items: Array<Record<string, unknown>>) => Promise<void>,
) => {
  const [form] = Form.useForm();
  const [mode, setMode] = React.useState<EditableTableMode>(
    EditableTableMode.View,
  );
  const [editKeys, setEditKeys] = React.useState<React.Key[]>([]);
  const [checkedRowKeys, setCheckedRowKeys] = React.useState<React.Key[]>([]);

  const isEditing = mode === EditableTableMode.Edit;

  const arrayStringFields = React.useMemo(
    () =>
      new Set(
        configs
          .filter((c) => c.editable && c.type === "arrayString")
          .map((c) => c.dataIndex ?? c.key),
      ),
    [configs],
  );

  const editableConfigs = React.useMemo(
    () => configs.filter((c) => c.editable),
    [configs],
  );

  const getUpdateValues = React.useCallback(() => {
    const fieldValues = form.getFieldsValue();
    const updateKeys: Array<Record<string, unknown>> = [];
    editKeys.forEach((key) => {
      const item: Record<string, unknown> = { [rowKey]: key };
      for (const property in fieldValues) {
        const [dataIndex, keyValue] = property.split("_");
        if (keyValue === key) {
          if (arrayStringFields.has(dataIndex)) {
            item[dataIndex] = fieldValues[property]?.split(",") ?? [];
          } else {
            item[dataIndex] = fieldValues[property];
          }
        }
      }
      updateKeys.push(item);
    });
    return updateKeys;
  }, [arrayStringFields, editKeys, form, rowKey]);

  const saveData = React.useCallback(async () => {
    const updateValues = getUpdateValues();
    if (updateValues.length === 0) return;
    await onSave(updateValues);
    setEditKeys([]);
    setCheckedRowKeys([]);
    openNotification();
  }, [getUpdateValues, onSave]);

  const onButtonEditClick = React.useCallback(async () => {
    switch (mode) {
      case EditableTableMode.View: {
        setMode(EditableTableMode.Edit);
        const fieldValues: Record<string, unknown> = {};
        dataSource.forEach((row) => {
          const record = row as Record<string, unknown>;
          const keyValue = record[rowKey];
          editableConfigs.forEach((config) => {
            const field = config.dataIndex ?? config.key;
            const value = record[config.key];
            fieldValues[`${field}_${keyValue}`] =
              config.type === "arrayString"
                ? (value as string[] | undefined)?.join(",")
                : value;
          });
        });
        form.setFieldsValue(fieldValues);
        break;
      }
      case EditableTableMode.Edit:
        await saveData();
        setMode(EditableTableMode.View);
        break;
      default:
        break;
    }
  }, [dataSource, editableConfigs, form, mode, rowKey, saveData]);

  const onButtonCancelClick = React.useCallback(async () => {
    setEditKeys([]);
    setCheckedRowKeys([]);
    setMode(EditableTableMode.View);
  }, []);

  const onRowSelectionChange = React.useCallback(
    (selectedRowKeys: React.Key[]) => {
      setCheckedRowKeys(selectedRowKeys);
    },
    [],
  );

  const rowSelection = React.useCallback((): TableRowSelection<T> | null => {
    if (mode === EditableTableMode.Edit) {
      return { type: "checkbox", onChange: onRowSelectionChange };
    }
    return null;
  }, [mode, onRowSelectionChange]);

  const onFormValueChanges = React.useCallback(
    (changesValue: Record<string, unknown>) => {
      const keys = editKeys;
      for (const property in changesValue) {
        const [dataIndex, key] = property.split("_");
        if (keys.includes(key) === false) {
          keys.push(key);
        }

        if (checkedRowKeys.length > 1) {
          const fieldValues: Record<string, unknown> = {};
          checkedRowKeys.forEach((rk) => {
            if (keys.includes(rk) === false) {
              keys.push(rk);
            }
            fieldValues[`${dataIndex}_${rk}`] = changesValue[property];
          });
          form.setFieldsValue(fieldValues);
        }
      }
      setEditKeys(keys);
    },
    [checkedRowKeys, editKeys, form],
  );

  return {
    form,
    isEditing,
    onButtonEditClick,
    onButtonCancelClick,
    rowSelection,
    onFormValueChanges,
  };
};
